import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ModalData } from '@datachain/ui-sdk/components/app-modal';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { ActionIconsInjectable } from '../../../../ui/ui.module';
import {
	ColumnConfigErrorKey,
	ColumnConfigListError,
	ExpositionColumnConfigListEntity,
} from '../../../domain/exposition-column-config-list.entity';
import { ExpositionColumnConfigEntity } from '../../../domain/exposition-column-config.entity';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../../providers/exposition.provider';
import {
	closeEditExpositionColumnsModal,
	ExpositionsSelector,
	getExpositionColumns,
	refreshExpositionColumns,
	saveExpositionColumns,
} from '../../../store';
import { ExpositionColumnsConfigExtendedComponent } from '../../exposition-columns-config-extended/exposition-columns-config-extended.component';
import { ExpositionColumnsConfigComponent } from '../../exposition-columns-config/exposition-columns-config.component';

enum ColumnConfigStep {
	ColumnsSelection = 0,
	ColumnsConfig = 1,
}

@Component({
	selector: 'app-exposition-columns-modal',
	templateUrl: './exposition-columns-modal.component.html',
	styleUrls: ['./exposition-columns-modal.component.scss'],
	providers: [ExpositionProvider],
})
export class ExpositionColumnsModalComponent<T extends { expositionId: number }>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ColumnConfigStep = ColumnConfigStep;

	public list$ = new ReplaySubject<ExpositionColumnConfigListEntity>(1);

	public vo$: Observable<{
		list: ExpositionColumnConfigListEntity;
		columnsConfig: ExpositionColumnConfigListEntity;
		currentStep: ColumnConfigStep;
		listErrors: Map<ColumnConfigErrorKey, ColumnConfigListError>;
		canEdit: boolean;
		currentError: ExpositionDiagnosticError | undefined;
	}>;

	@ViewChild(MatStepper) public stepper: MatStepper | null = null;
	@ViewChild('columnsSelectionGrid')
	public readonly columnsSelectionCmp: ExpositionColumnsConfigComponent | null =
		null;
	@ViewChild(ExpositionColumnsConfigExtendedComponent)
	public columnsConfigCmp: ExpositionColumnsConfigExtendedComponent | null = null;

	private readonly colConfigListErrorsSubject = new BehaviorSubject<
		Map<ColumnConfigErrorKey, ColumnConfigListError>
	>(Map());

	private readonly currentStep$ = new BehaviorSubject<ColumnConfigStep>(
		ColumnConfigStep.ColumnsConfig
	);

	private readonly previousStep$ = new ReplaySubject<ColumnConfigStep>(1);

	private readonly errorsByStep: Record<
		ColumnConfigStep,
		Array<ExpositionErrorKey>
	> = {
		[ColumnConfigStep.ColumnsSelection]: [ExpositionErrorKey.NoActiveColumns],
		[ColumnConfigStep.ColumnsConfig]: [
			ExpositionErrorKey.DuplicatedAliasColumns,
			ExpositionErrorKey.InvalidColumns,
		],
	};

	public constructor(
		private readonly store: Store,
		private readonly expositionsSelector: ExpositionsSelector,
		private readonly expositionProvider: ExpositionProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'edit-exposition-columns';

		this.store.dispatch(
			getExpositionColumns({
				expositionId: this.modalData.expositionId,
			})
		);

		this.expositionsSelector
			.getColumns$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320), // time to Devextreme finished loading the list view
				tap((columns) => {
					this.list$.next(columns);
				})
			)
			.subscribe();

		const errors$ = combineLatest([
			this.expositionProvider.errors$,
			this.currentStep$,
		]).pipe(
			map(([currentErrors, currentStep]) =>
				currentErrors.filter((_, key) =>
					this.errorsByStep[currentStep].includes(key)
				)
			)
		);

		this.vo$ = combineAll({
			list: this.list$,
			columnsConfig: this.list$.pipe(
				map((list) => {
					const selectedColumns = list.elements.filter((col) => col.isActive);
					return list.setElements(selectedColumns);
				})
			),
			currentStep: this.currentStep$,
			listErrors: this.colConfigListErrorsSubject,
			canEdit: this.expositionsSelector.getCanEditExposition$(),
			currentError: errors$.pipe(map((errors) => errors.valueSeq().first())),
		});
	}

	public close(): void {
		this.store.dispatch(
			closeEditExpositionColumnsModal({
				expositionId: this.modalData.expositionId,
			})
		);
	}

	public backStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.previous();
	}

	public nextStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.next();
	}

	public changeCurrentStep(event: StepperSelectionEvent): void {
		this.currentStep$.next(event.selectedIndex);
		this.previousStep$.next(event.previouslySelectedIndex);
	}

	public async onStepReady(): Promise<void> {
		const previousStep = await firstValueFrom(this.previousStep$);
		if (
			this.columnsConfigCmp &&
			previousStep === ColumnConfigStep.ColumnsConfig
		) {
			const list = await firstValueFrom(this.list$);
			const updatedList = this.columnsConfigCmp.getUpdatedList();

			const updatedElements = list.elements.reduce<
				Map<string, ExpositionColumnConfigEntity>
			>((acc, curr) => {
				const item = updatedList.elements.get(curr.id);

				if (!item) {
					return acc.set(
						curr.id,
						curr.position !== 0 ? curr.updatePosition(0) : curr
					);
				}
				return acc.set(curr.id, item);
			}, Map());
			this.list$.next(updatedList.setElements(updatedElements));
		}
	}

	public modifyColumnConfigValidity(
		errors: Map<ColumnConfigErrorKey, ColumnConfigListError>
	): void {
		if (!this.columnsSelectionCmp) {
			return;
		}
		this.list$.next(this.columnsSelectionCmp.getUpdatedList());
		this.colConfigListErrorsSubject.next(errors);
	}

	public save(): void {
		if (!this.columnsConfigCmp) {
			return;
		}
		const columns = this.columnsConfigCmp.getUpdatedList();
		this.store.dispatch(
			saveExpositionColumns({
				expositionId: this.modalData.expositionId,
				columns,
			})
		);
	}

	public fetchColumns(): void {
		this.store.dispatch(
			refreshExpositionColumns({
				expositionId: this.modalData.expositionId,
			})
		);
	}
}
